// ProtectedRoute.jsx
//
// Lightweight per-route gate. AuthGate (mounted between <Router> and the
// app shell) already runs the 500ms session-rehydration wait + the
// unauthenticated UX (blurred backdrop + Login). By the time any
// ProtectedRoute mounts, the session is known.
//
// Therefore this component does NOT render a spinner. Page-load spinners
// violate the project's UX rule (skeletons over spinners; reflect content
// shape, never block with a generic ring).
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { toast } from 'sonner';

const ProtectedRoute = ({ children }) => {
  const session = useSession();

  // Surface refresh failures (custom event from src/index.js auth plumbing)
  // so the user sees WHY they got bounced. Without this, Samuel reported
  // "I dont see it but try to reload" — silent UI breakage on token loss.
  useEffect(() => {
    const onSessionLost = () => {
      toast.error('Session expired', {
        description: 'Please sign in again.',
        duration: 6000,
      });
    };
    window.addEventListener('pog:session-lost', onSessionLost);
    return () => window.removeEventListener('pog:session-lost', onSessionLost);
  }, []);

  // Session resolved (AuthGate guarantees this) → render or redirect.
  if (session === null) {
    return <Navigate to="/login" replace />;
  }
  // session === undefined briefly during hydration. AuthGate already
  // handled this with a backdrop, but render children here is fine since
  // each page now uses its own structural skeleton on data load.
  return children;
};

export default ProtectedRoute;
